import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import Box from "@mui/material/Box"
import CardContent from "@mui/material/CardContent"
import Typography from "@mui/material/Typography"

import type { ValidateOAuthRes } from "types/auth.types"

import { AccountCard, LogoContainer } from "widgets/styled/containers"
import { useValidateOAuthMutation, useAcceptUserTermsMutation } from "features/api"
import { setCredentials } from "features/store/authSlice"
import TermsAndConditionsModal from "widgets/user/TermsAndConditionsModal"
import { DEFAULT_TARGET, IMAGOTYPE, PREVIOUS_LOCATION_KEY } from "helpers/utils/constants"
import CustomLink from "widgets/common/CustomLink"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import { getNewTokenExpirationAt } from "helpers/utils/dateUtils"

function LoginOAuth() {
  const calledSendOauthData = useRef(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const urlParams = useMemo(() => {
    return new URLSearchParams(window.location.search)
  }, [])

  const [validateOAuth, { isLoading }] = useValidateOAuthMutation()
  const [acceptUserTerms] = useAcceptUserTermsMutation()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [username, setUsername] = useState("")
  const [accessToken, setAccessToken] = useState("")
  const [openTC, setOpenTC] = useState(false)
  const handleTCClose = () => {
    setOpenTC(false)
  }

  const verifyUser = useCallback(
    (user: ValidateOAuthRes) => {
      if (user.sub === "terms") {
        setUsername(user.username)
        setAccessToken(user.access_token)
        setOpenTC(true)
      } else {
        const newTokenExpirationAt = getNewTokenExpirationAt()
        dispatch(
          setCredentials({
            user: user.username,
            token: user.access_token,
            tokenExpirationAt: newTokenExpirationAt,
            globalRole: user.role,
          }),
        )
        const target = sessionStorage.getItem(PREVIOUS_LOCATION_KEY) ?? DEFAULT_TARGET
        navigate(target)
      }
    },
    [dispatch, navigate],
  )

  const sendOAuthData = useCallback(async () => {
    const oAuthData = {
      code: urlParams.get("code") || "",
      state: urlParams.get("state") || "",
    }
    validateOAuth(oAuthData)
      .unwrap()
      .then(verifyUser)
      .catch((error) => {
        const message =
          error.status === 404 || error.status === 401 || !error.data
            ? t("login.OAUTH_VALIDATION_FAILED")
            : error.data.message
        setErrorMessage(message)
      })
  }, [t, urlParams, validateOAuth, verifyUser])

  useEffect(() => {
    if (!calledSendOauthData.current) {
      sendOAuthData()
      calledSendOauthData.current = true
    }
    // We need to trigger sendOAuthData only once, react 18 in dev with listing the dependency calls it twice immidiately, and fails, then calls it a third time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTermsAndConditions = useCallback(async () => {
    await snackbarMutation({
      mutation: acceptUserTerms({ username: String(username), accessToken }).unwrap(),
      getErrorMessage: buildGetErrorMessage(
        t("error.UPDATING_ITEM", {
          item: t("generic.TERMS_AND_CONDITIONS").toLowerCase(),
        }),
      ),
      getSuccessMessage: () =>
        t("success.UPDATING_ITEM", {
          item: t("generic.TERMS_AND_CONDITIONS"),
          count: 2,
          context: "male",
        }),
    })
      .then(verifyUser)
      .catch(() => {})
  }, [username, acceptUserTerms, accessToken, t, verifyUser])

  return (
    <>
      <AccountCard
        sx={{ maxWidth: 360, minWidth: 280, width: "50%", textAlign: "center" }}
        elevation={4}
      >
        <LogoContainer disableGutters>
          <Box component="img" src={IMAGOTYPE} sx={{ width: "60%" }} />
        </LogoContainer>
        <CardContent sx={{ p: 4 }}>
          {isLoading && (
            <Typography
              variant="body2"
              component="p"
              align="center"
              sx={{ mb: 2, mt: 1 }}
            >
              {t("login.VALIDATING_DATA")}
            </Typography>
          )}
          {errorMessage && (
            <Typography
              variant="body2"
              component="p"
              align="center"
              color="error"
              sx={{ mb: 2, mt: 1 }}
            >
              {errorMessage}
            </Typography>
          )}
          <CustomLink
            href="/accounts/login"
            underlineHover
            variant="body2"
            styles={{ mt: 4 }}
          >
            {t("generic.RETURN_TO") + t("login.LOGIN_PAGE")}
          </CustomLink>
        </CardContent>
      </AccountCard>
      <TermsAndConditionsModal
        open={openTC}
        onClose={handleTCClose}
        onComplete={handleTermsAndConditions}
        username={username}
        accessToken={accessToken}
      />
    </>
  )
}

export default LoginOAuth
